import { DeleteOutlined, EditOutlined, EyeOutlined, FileDoneOutlined, MoreOutlined } from '@ant-design/icons';
import type { TableColumnsType, TableProps } from 'antd';
import { Button, Dropdown, Menu, message, Space, Table, Tag } from 'antd';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, TEXT_BUTTON_HEADER_CREATE } from '../../../config/config';
import useQueryParams, { buildParamsFromQuery, buildParamsFromQueryKernel } from '../../../hooks/useQueryParams';
import CustomerModel from '../../../models/CustomerModel';
import { PermissionCustomer } from '../../../permissions/PermissionCustomer';
import { PermissionSubChannel } from '../../../permissions/PermissionSubChannel';
import customerStore from '../../../stores/customerStore';
import subChannel from '../../../stores/subChannel';
import { formatDateString } from '../../../utils/dateFunctions';
import { exportToExcel } from '../../../utils/excelFunction';
import { getSortField, getSortOrder } from '../../../utils/functions';
import { StatusActive } from '../../../utils/selects';
import { getStatusColor } from '../../../utils/statusColorMapper';
import { formatDocument } from '../../../utils/TextFormatters';
import BreadCrumbComponent from '../../atoms/BreadCrumbComponent';
import ActionButton from '../../atoms/ButtonComponent';
import InputComponet from '../../atoms/InputComponet';
import PermissionComponent from '../../atoms/PermissionComponent';
import SelectComponent from '../../atoms/SelectComponent';
import ContentBox from '../../molecules/ContentBox';
import Permission from '../../molecules/Permission';
import ModalCliente from '../../organisms/ModalCliente';
import ModalComponent from '../../organisms/ModalComponent';
import { useAuth } from '../../protected/ProviderAuth';

interface DataType {
    user: any;
    subChannel: any;
    channel: any;
    active: boolean | React.Key;
    document: any;
    firstName: any;
    key: React.Key;
    nameTag: string;
    responsive: boolean;
    tag: string;
    uuid: string;
}

// BreadCrumb
const breadCrumb = [
    {
        title: <Link to="/dashboard">Dashboard</Link>,
    },
    {
        title: <p>Listagem de Clientes</p>,
    },
]

const ListCustomer: React.FC = observer(() => {
    const { customerList, loading, page, totalElements, defaultparams, sort, size } = customerStore;
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [itemSelected, setItemSelected] = useState<any>();
    const [selectionCheckbox, setSelectionCheckbox] = useState<any>();
    const [queryParams, setQueryParams] = useQueryParams();
    const { hasPermission, isColumnVisible } = useAuth();
    const navigate = useNavigate();
    const listRouter = () => { navigate(`/cliente/cadastro`); }
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
    const [isModalCustomerDeleteOpen, setIsModalCustomerDeleteOpen] = useState(false);
    const [itemSelectedDelete, setIsDeleteModalOpen] = useState<any>();
    const initialValues: any = {
        name: queryParams.name || '',
        document: queryParams.document || '',
        seller: queryParams.seller || '',
        clientExternal: queryParams.clientExternal || '',
        active: typeof queryParams?.active === 'string'
            ? { value: queryParams?.active, label: queryParams?.active }
            : { value: '', label: '' },
        entity: typeof queryParams?.entity === 'string'
            ? { value: queryParams?.entity, label: queryParams?.entity }
            : { value: '', label: '' },
    };

    const [formValues, setFormValues] = useState(initialValues);

    const mappings = {
        page: 'page',
        size: 'size',
        sort: 'sort',
        name: 'name',
        document: 'document',
        email: 'email',
        entity: 'entity'
    };

    const defaultparamsList = {
        size: 999999,
        page: 0,
        sort: 'created,desc',
        search: '',
    }

    useEffect(() => {
        const params = buildParamsFromQueryKernel(queryParams, mappings);

        const fetchData = async () => {
            if (hasPermission(PermissionCustomer.GET)) {
                await customerStore.getList(params);
            }

            if (hasPermission(PermissionSubChannel.GET)) {
                await subChannel.getList(defaultparamsList);
            }
        };

        fetchData();
    }, []);

    const actionMenu = (record: any) => (
        <Menu
            items={[
                {
                    key: 'view',
                    label:
                        <>
                            {isColumnVisible(PermissionCustomer.GET_UUID) &&
                                <div className="cursor-pointer flex gap-2" onClick={() => showModal(record)}>
                                    <div>
                                        <div className="flex gap-2"><EyeOutlined />Visualizar</div>
                                    </div>
                                </div>
                            }
                        </>
                },
                {
                    key: 'order',
                    label:
                        <a href={`/cliente/detalhes/${record.uuid}`}>
                            <div className="cursor-pointer flex gap-2">
                                <div className="flex gap-2"><FileDoneOutlined />Ver Pedido</div>
                            </div>
                        </a>
                },
                {
                    key: 'editar',
                    label:
                        <>
                            {isColumnVisible(PermissionCustomer.GET_UUID) &&
                                <a href={`/cliente/editar/${record.uuid}`}>
                                    <div className="cursor-pointer flex gap-2">
                                        <div className="flex gap-2"><EditOutlined /> Editar</div>
                                    </div>
                                </a>
                            }
                        </>
                },
                // {
                //     key: 'delete',
                //     label:
                //         <>
                //             {isColumnVisible(PermissionCustomer.DELETE) &&
                //                 <div className="cursor-pointer aligm-items " onClick={() => showDeleteModal(record)}>
                //                     <div className="flex gap-2">
                //                         <DeleteOutlined />
                //                         <p>Deletar</p>
                //                     </div>
                //                 </div>
                //             }
                //         </>
                // },
            ]}
        />
    )

    // Colunas da Tabela
    const columns: TableColumnsType<DataType> = [
        {
            title: 'Nome Fantasia',
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: true,
            defaultSortOrder: getSortOrder('firstName', queryParams.sort),
        },
        {
            title: 'Documento',
            dataIndex: 'document',
            key: 'document',
            sorter: true,
            defaultSortOrder: getSortOrder('document', queryParams.sort),
            render: (text) => {
                return (
                    <>{<p>{formatDocument(text)}</p>}</>
                );
            },
        },
        {
            title: 'E-mail',
            dataIndex: 'email',
            key: 'email',
            sorter: true,
            defaultSortOrder: getSortOrder('email', queryParams.sort),
            render: (text, record) => {
                return (
                    <>{<p>{record?.user?.email}</p>}</>
                );
            },
        },
        {
            title: 'Tipo de Cadastro',
            dataIndex: 'entity',
            key: 'entity',
            sorter: true,
            defaultSortOrder: getSortOrder('entity', queryParams.sort),
        },
        {
            title: 'Data de criação',
            dataIndex: 'created',
            key: 'created',
            sorter: true,
            defaultSortOrder: getSortOrder('created', queryParams.sort),
            render: (text) => {
                return (
                    <>{<p>{formatDateString(text)}</p>}</>
                );
            },
        },
        {
            title: 'Ativos',
            dataIndex: 'active',
            key: 'active',
            sorter: true,
            filters: [
                { text: 'Ativo', value: true },
                { text: 'Inativo', value: false },
            ],
            onFilter: (value, record) => record.active === value,
            defaultSortOrder: getSortOrder('active', queryParams.sort),
            render: (active) => {
                let text = active ? 'Ativo' : 'Inativo';
                return (
                    <Tag color={getStatusColor(text)}><p>{text}</p></Tag>
                );
            },
        },
        {
            title: 'Ações',
            key: 'operation',
            width: 60,
            render: (_, record) => (
                <Dropdown overlay={actionMenu(record)} placement="bottomRight" trigger={['click']}>
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        }
    ];

    const showModal = (record: any) => {
        setItemSelected(record);
        setIsModalOpen(true);
    };


    const showDeleteModal = (record: any) => {
        setIsDeleteModalOpen(record);
        setIsModalCustomerDeleteOpen(true);
    };


    // Faz o filtro da tabela
    const handleSubmit = async (values: any) => {
        const newParams: any = {
            name: values?.name || undefined,
            document: values?.document.replace(/\D/g, '') || undefined,
            email: values?.email || undefined,
            active: values?.active?.value || undefined,
            entity: values?.entity?.value || undefined
        };

        setQueryParams(newParams);
        await customerStore.getList(newParams);
    };

    // Interação da Tabela como sorte
    const onChange: TableProps<DataType>['onChange'] = async (pagination, filters, sorter, extra) => {
        const getSort = getSortField(sorter);
        const currentPage = pagination.current ? pagination.current - 1 : 0;
        const newPageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;

        const params = {
            size: newPageSize || queryParams.size,
            page: currentPage ?? queryParams.page ?? 0,
            sort: getSort || queryParams.sort || 'created,desc',
        };

        setQueryParams({
            size: params.size,
            page: params.page,
            sort: params.sort,
        });

        //@ts-ignore
        await customerStore.getList(params);
    };


    // Limpa os dados do formulario
    const handleClearAndSearch = async (resetForm: any) => {
        const clearedValues = {
            name: '',
            document: '',
            active: null,
            // adicione os outros campos que você usa
        };

        setFormValues(clearedValues); // força o Formik a reinicializar
        resetForm({ values: clearedValues });
        setQueryParams({});
        await customerStore.getList(defaultparams);
    };

    const handleExport = async () => {
        const params = buildParamsFromQuery(queryParams, mappings);
        const exportParams = {
            ...params,
            size: 999999,
        }

        const dataExport = await customerStore.getList(exportParams);

        if (dataExport) {
            const listExport = dataExport.data.content.map((item: CustomerModel) => ({
                firstName: item.firstName,
                corporateName: item.corporateName,
                document: item.document,
                customerType: item?.customerType?.name,
                paymentForm: item.paymentForm,
                paymentDeadline: item.paymentDeadline,
                email: item.user ? item?.user?.email : '',
                emailNf: item.emailNf,
                contactName: item.contactName,
                mobile: item.mobile,
                mobileWhatsapp: item?.mobileWhatsapp === true ? 'Sim' : 'Não',
                phone: item.phone,
                businessContact: item.businessContact,
                phoneCommercial: item.phoneCommercial,
                cellphoneCommercial: item.cellphoneCommercial,
                contactFinancial: item.contactFinancial,
                phoneFinancial: item.phoneFinancial,
                cellphoneFinancial: item.cellphoneFinancial,
                billingAddress: '-',
                streetBillingAddress: item?.billingAddress?.street,
                numberBillingAddress: item?.billingAddress?.number,
                blockBillingAddress: item?.billingAddress?.block,
                cityBillingAddress: item?.billingAddress?.city,
                stateBillingAddress: item?.billingAddress?.state,
                countryBillingAddress: item?.billingAddress?.country,
                addressComplemenBillingAddresst:
                    item?.billingAddress?.addressComplement,
                zipCodeBillingAddress: item?.billingAddress?.zipCode,
                mainAddress: '-',
                streetMainAddress: item?.mainAddress?.street,
                numberMainAddress: item?.mainAddress?.number,
                blockMainAddress: item?.mainAddress?.block,
                cityMainAddress: item?.mainAddress?.city,
                stateMainAddress: item?.mainAddress?.state,
                countryMainAddress: item?.mainAddress?.country,
                addressComplementMainAddress: item?.mainAddress?.addressComplement,
                zipCodeMainAddress: item?.mainAddress?.zipCode,
                status: item?.status,
                active: item.active ? "Sim" : "Não",
                created: formatDateString(item.created?.toString() ?? ""),
                externalId: item?.externalId,
            }));

            exportToExcel(listExport, [
                'Nome Fantasia',
                'Razão Social',
                'CNPJ',
                'Identificador de Preço',
                'Formulário de Pagamento',
                'Prazo de Pagamento',
                'Email',
                'Email Nota',
                'Nome Contato',
                'Celular',
                'Whatsapp',
                'Telefone Fixo',
                'Contato Comercial',
                'Telefone Comercial',
                'Celular Comercial',
                'Contato Financeiro',
                'Telefone Financeiro',
                'Celular Financeiro',
                'Endereço de Entrega',
                'Endereço',
                'Número',
                'Bairro',
                'Complemento',
                'Cidade',
                'Estado',
                'Pais',
                'CEP',
                'Endereço de Cobrança',
                'Endereço',
                'Número',
                'Bairro',
                'Complemento',
                'Cidade',
                'Estado',
                'Pais',
                'CEP',
                'Status do Cadastro',
                'Status',
                'Data de Criação',
                'Código BP',
            ], "Tabela de Cliente.xlsx");
        }
    };

    const handleDeleteItem = async () => {
        const response = await customerStore.deleteCustomer(itemSelectedDelete.uuid);
        if (!response?.error) {
            await customerStore.getList(defaultparams);
            setIsModalCustomerDeleteOpen(false);
        }
    };

    const deleteSelectedItems = async () => {
        try {
            if (selectionCheckbox !== undefined) {
                const results = await Promise.all(
                    selectionCheckbox.map((item: any) => customerStore.deleteCustomersAll(item.uuid))
                );
                const hasError = results.some(response => response?.error);
                if (hasError) {
                    message.error('Erro ao deletar um ou mais Clientes.');
                    setSelectionCheckbox([]);
                } else {
                    message.success('Todos os Clientes foram deletados com sucesso!');
                    await customerStore.getList(defaultparams);
                    setSelectionCheckbox([]);
                }
                setIsModalDeleteOpen(false);
            } else {
                message.error('Selecione pelo menos um item da listagem');
            }
        } catch (error) {
            message.error('Erro ao tentar deletar os Clientes.');
        }
    };

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
            setSelectionCheckbox(selectedRows);
        },
    };

    const deleteAllModal = () => {
        if (selectionCheckbox?.length > 0) {
            setIsModalDeleteOpen(true);
        } else {
            message.warning('Por favor, selecione ao menos um item para realizar a exclusão.');
        }
    }


    return (
        <>
            <BreadCrumbComponent breadCrumbList={breadCrumb} permission={PermissionCustomer.POST} textButton={TEXT_BUTTON_HEADER_CREATE} onClick={() => listRouter()} />

            <Permission permissionKeys={[PermissionCustomer.GET]} fallback={<PermissionComponent />}>
                <ContentBox className="mb-4 p-6" title="Cliente" description='Tela de listagem de Clientes'>
                    <Formik
                        initialValues={formValues}
                        onSubmit={handleSubmit}
                    >
                        {({ resetForm }) => (
                            <Form>
                                <div className="flex items-end w-full gap-4 pb-4">
                                    <div className="w-full">
                                        <InputComponet label="Nome Fantasia" name="name" id="name" />
                                    </div>
                                    <div className="w-full">
                                        <InputComponet label="Documento" name="document" id="document" />
                                    </div>
                                    <div className="w-full">
                                        <SelectComponent label="Status" name="active" options={StatusActive} />
                                    </div>
                                </div>


                                <div className="grid grid-cols-12 items-end w-full gap-4 pb-4">
                                    <div className="col-span-4">
                                        <InputComponet label="E-mail" name="email" id="email" />
                                    </div>
                                    <div className="col-span-4">
                                        <SelectComponent label="Tipo de Cadastro" name="entity" options={[
                                            { label: "Física", value: "FISICA" },
                                            { label: "Jurídica", value: "JURIDICA" },
                                        ]} />
                                    </div>
                                </div>

                                <div className="flex items-end gap-4 justify-end ">
                                    <Button onClick={() => handleClearAndSearch(resetForm)}>
                                        Limpar
                                    </Button>
                                    <Button htmlType="submit" type="primary">Filtrar</Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </ContentBox>

                <ContentBox className="mb-4 p-6" description='Tela de listagem de Cliente' actions={
                    <>
                        {/* <Permission permissionKeys={[PermissionCustomer.DELETE]}><ActionButton icon actionType="delete" onClick={() => deleteAllModal()}>Deletar</ActionButton></Permission> */}
                        <ActionButton icon actionType="export" onClick={() => handleExport()}>Exportar para Excel</ActionButton>
                    </>
                }>
                    <Table
                        className="table-custom"
                        columns={columns}
                        dataSource={customerList.map((item: any, index: number) => ({
                            ...item,
                            key: item.uuid || index,
                        }))}
                        onChange={onChange}
                        loading={loading}
                        size="middle"
                        bordered={false}
                        pagination={{
                            current: page + 1,
                            pageSize: size,
                            total: totalElements,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '40', '60'],
                        }}
                        showSorterTooltip={{ target: 'sorter-icon' }}
                    />
                </ContentBox>
            </Permission>

            {isModalOpen && <ModalCliente setIsModalOpen={setIsModalOpen} isModalOpen={isModalOpen} customerUuid={itemSelected.uuid} />}

            {isModalCustomerDeleteOpen && <ModalComponent
                title="Aviso"
                content={<p>Você deseja excluir o Cliente <b>{itemSelectedDelete?.firstName}</b>?</p>}
                isModalOpen={isModalCustomerDeleteOpen}
                handleOk={handleDeleteItem}
                handleCancel={() => setIsModalCustomerDeleteOpen(false)}
            />}

            <ModalComponent
                title="Aviso"
                content={<p>Você está prestes a excluir vários itens da listagem, deseja prosseguir?</p>}
                isModalOpen={isModalDeleteOpen}
                handleOk={deleteSelectedItems}
                handleCancel={() => setIsModalDeleteOpen(false)}
            />
        </>
    );
});

export default ListCustomer;